/**
 * Weekoverzicht per PL (Joris, 3-4 sep 2026) — de Airtable-tijdlijnview "Weekoverzicht
 * per PL" in de nieuwe tool: alle taken en deadlines van de projecten waar jij
 * projectleider van bent, óók de taken die anderen uitvoeren.
 *
 * Opzet zoals in Airtable: venster vanaf vandaag, gegroepeerd op startdatum → project,
 * per taak een balk (start t/m eind) met het volledige label — projectnummer-taak ·
 * prio · deadline · uren · uitvoerder — dat buiten de balk doorloopt als de balk kort
 * is. Standaardfilter zoals de view: Gearchiveerd / Opgestuurd / Afspraak nooit,
 * "wacht op feedback / n.v.t." uit, verleden uit; afgerond blijft zichtbaar (groen).
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightSmallIcon from '@mui/icons-material/ChevronRight';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';
import { PRIO_COLORS, TYPE_COLORS } from '../../utils/taakKleuren';

const FONT = '"Inter", system-ui, sans-serif';
const NAV_FONT = '"Manrope", system-ui, sans-serif';
const LABEL_BREEDTE = 260;
const RIJ_HOOGTE = 34;
const WACHT = new Set(['Nog niet van toepassing', 'Wacht op feedback']);
const NOOIT = new Set(['Gearchiveerd', 'Opgestuurd', 'Afspraak']); // Airtable-filter: PrioNr is not …
const DAGNAMEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// Zelfde kleurenkaart als Dagstart, Weekplanning en Overzicht per project (utils/taakKleuren),
// zelfde bloklook als TaakBlok: lichte tint met een kleurstreep links, donkere tekst.
const tint = (hex) => `${hex}22`;
const rand = (hex) => `${hex}88`;

const isoLokaal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const vanIso = (iso) => new Date(`${iso}T12:00:00`);
const plusDagen = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dmj = (iso) => { if (!iso) return ''; const d = vanIso(iso); return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`; };
const kortDatum = (iso) => { if (!iso) return ''; const d = vanIso(iso); return `${DAGNAMEN[d.getDay()]} ${d.getDate()} ${MAANDEN[d.getMonth()]}`; };
const lees = (k, std) => { try { const v = localStorage.getItem(k); return v === null ? std : JSON.parse(v); } catch { return std; } };
const bewaar = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* privémodus */ } };
const uren = (u) => (u == null ? '' : Number.isInteger(u) ? `${u}.0` : String(u));

const kleurVan = (t) => (t.afgerond ? PRIO_COLORS.Afgerond : (PRIO_COLORS[t.prio] || TYPE_COLORS[t.type] || '#94a3b8'));

export default function WeekoverzichtPL() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const mijnNaam = accounts[0]?.name || '';
  const vandaag = isoLokaal(new Date());
  const [pls, setPls] = useState(null);
  const [pl, setPl] = useState(() => lees('weekpl:pl', ''));
  const [begin, setBegin] = useState(() => new Date());
  const [dagenAantal, setDagenAantal] = useState(() => lees('weekpl:dagen', 8));
  const [verbergVerleden, setVerbergVerleden] = useState(() => lees('weekpl:verleden', true));
  const [verbergAfgerond, setVerbergAfgerond] = useState(() => lees('weekpl:afgerond2', false));
  const [verbergWacht, setVerbergWacht] = useState(() => lees('weekpl:wacht', true));
  const [zoek, setZoek] = useState('');
  const [dicht, setDicht] = useState(() => new Set());
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  // De kaart vult de rest van het venster (hoogte gemeten vanaf haar bovenkant), zodat
  // de horizontale scrollbar áltijd onderaan in beeld staat (Joris, 4 sep).
  const kaartRef = useRef(null);
  const [kaartTop, setKaartTop] = useState(320);
  useLayoutEffect(() => {
    const meet = () => { if (kaartRef.current) setKaartTop(Math.round(kaartRef.current.getBoundingClientRect().top + window.scrollY)); };
    meet();
    window.addEventListener('resize', meet);
    return () => window.removeEventListener('resize', meet);
  }, [pls, data]);

  useEffect(() => { bewaar('weekpl:dagen', dagenAantal); bewaar('weekpl:verleden', verbergVerleden); bewaar('weekpl:afgerond2', verbergAfgerond); bewaar('weekpl:wacht', verbergWacht); }, [dagenAantal, verbergVerleden, verbergAfgerond, verbergWacht]);
  useEffect(() => { if (pl) bewaar('weekpl:pl', pl); }, [pl]);

  // PL-lijst + wie ben ik
  useEffect(() => {
    let dood = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/weekoverzicht-pl/pls?userName=${encodeURIComponent(mijnNaam)}`);
        const d = await leesJson(r, 'Projectleiders laden');
        if (dood) return;
        const lijst = d.pls || [];
        setPls(lijst);
        setPl((huidig) => {
          if (huidig && lijst.some((x) => x.naam === huidig)) return huidig;
          if (d.default) return d.default;
          const voornaam = mijnNaam.split('|')[0].trim().split(' ')[0].toLowerCase();
          const match = lijst.filter((x) => x.naam.toLowerCase().startsWith(voornaam));
          return match.length === 1 ? match[0].naam : (lijst[0]?.naam || '');
        });
      } catch (e) { if (!dood) { setPls([]); setFout(e.message); } }
    })();
    return () => { dood = true; };
  }, [mijnNaam]);

  const beginIso = isoLokaal(begin);
  const dagen = useMemo(() => Array.from({ length: dagenAantal }, (_, i) => plusDagen(begin, i)), [begin, dagenAantal]);
  const dagIso = useMemo(() => dagen.map(isoLokaal), [dagen]);
  const eersteDag = dagIso[0]; const laatsteDag = dagIso[dagIso.length - 1];
  const n = dagIso.length;

  // Taken van het venster (de kern rekent in hele weken; we vragen ruim en knippen zelf)
  useEffect(() => {
    if (!pl) return undefined;
    let dood = false;
    setData(null); setFout(null);
    (async () => {
      try {
        const params = new URLSearchParams({ pl, start: beginIso, weeks: String(Math.ceil(dagenAantal / 7)) });
        const r = await authFetch(`${BACKEND_URL}/Api/weekoverzicht-pl?${params}`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
        if (!dood) setData(d);
      } catch (e) { if (!dood) { setFout(e.message); setData({ projecten: [], taken: [], deliverables: [] }); } }
    })();
    return () => { dood = true; };
  }, [pl, beginIso, dagenAantal]);

  const projectBij = useMemo(() => new Map((data?.projecten || []).map((p) => [p.id, p])), [data]);

  // Groepen: startdatum → project → taken (zoals de Airtable-view "Grouped by StartDateUitvoerder, Project")
  const groepen = useMemo(() => {
    if (!data) return [];
    const q = zoek.trim().toLowerCase();
    const raakt = (t) => { const s = t.start || t.eind; const e = t.eind || t.start; return s && s <= laatsteDag && e >= eersteDag; };
    const taken = (data.taken || []).filter((t) => {
      if (NOOIT.has(t.prio) || NOOIT.has(t.type)) return false;
      if (verbergWacht && WACHT.has(t.prio)) return false;
      if (verbergAfgerond && t.afgerond) return false;
      if (!raakt(t)) return false;
      if (verbergVerleden && (t.eind || t.start) < vandaag) return false;
      if (q) {
        const p = projectBij.get(t.projectId) || {};
        if (!`${t.naam} ${(t.wie || []).join(' ')} ${t.type || ''} ${t.prio || ''} ${p.nummer || ''} ${p.naam || ''} ${p.klant || ''}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
    const dels = (data.deliverables || []).filter((d) => !(verbergVerleden && d.deadline < vandaag));
    const perDatum = new Map();
    const zet = (datum, projectId, item, soort) => {
      const g = perDatum.get(datum) || { datum, projecten: new Map() };
      const p = g.projecten.get(projectId) || { project: projectBij.get(projectId) || { id: projectId, nummer: '', naam: '?' }, taken: [], deadlines: [] };
      p[soort].push(item); g.projecten.set(projectId, p); perDatum.set(datum, g);
    };
    taken.forEach((t) => zet(t.start || '', t.projectId, t, 'taken'));
    dels.forEach((d) => zet(d.deadline, d.projectId, d, 'deadlines'));
    return [...perDatum.values()]
      .sort((a, b) => (a.datum || '9').localeCompare(b.datum || '9'))
      .map((g) => ({
        ...g,
        projecten: [...g.projecten.values()]
          .sort((a, b) => String(a.project.nummer).localeCompare(String(b.project.nummer)))
          .map((p) => ({ ...p, taken: p.taken.sort((a, b) => String(a.naam).localeCompare(b.naam)) })),
      }));
  }, [data, zoek, verbergWacht, verbergAfgerond, verbergVerleden, vandaag, eersteDag, laatsteDag, projectBij]);

  const totaal = useMemo(() => {
    const alle = groepen.flatMap((g) => g.projecten.flatMap((p) => p.taken));
    const projecten = new Set(groepen.flatMap((g) => g.projecten.map((p) => p.project.id)));
    return {
      projecten: projecten.size, taken: alle.length,
      uren: Math.round(alle.reduce((s, t) => s + (t.uren || 0), 0) * 10) / 10,
      deadlines: groepen.reduce((s, g) => s + g.projecten.reduce((s2, p) => s2 + p.deadlines.length, 0), 0),
    };
  }, [groepen]);

  const idxVan = (iso) => dagIso.indexOf(iso);
  const balkPositie = (t) => {
    const s = t.start || t.eind; const e = t.eind || t.start;
    if (!s) return null;
    if (s > laatsteDag || e < eersteDag) return null;
    const van = s < eersteDag ? 0 : idxVan(s); const tot = e > laatsteDag ? n - 1 : idxVan(e);
    if (van < 0 || tot < 0) return null;
    return { van, tot, links: s < eersteDag, rechts: e > laatsteDag };
  };
  const toggleDicht = (sleutel) => setDicht((prev) => { const s = new Set(prev); if (s.has(sleutel)) s.delete(sleutel); else s.add(sleutel); return s; });
  const dagBreedte = dagenAantal <= 8 ? 230 : 130;

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="calendar_view_week" title="Weekoverzicht per PL"
        subtitle="Alle taken en deadlines van jouw projecten — ook wat anderen uitvoeren" />

      {/* ── Werkbalk ── */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Projectleider</InputLabel>
          <Select label="Projectleider" value={pls && pls.some((x) => x.naam === pl) ? pl : ''} onChange={(e) => setPl(e.target.value)}
            sx={{ fontFamily: FONT, fontSize: 14, bgcolor: '#fff' }}>
            {(pls || []).map((x) => <MenuItem key={x.naam} value={x.naam} sx={{ fontSize: 13.5 }}>{x.naam} <Box component="span" sx={{ color: '#94a3b8', ml: 0.75 }}>{x.projecten}</Box></MenuItem>)}
          </Select>
        </FormControl>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff', px: 0.5 }}>
          <IconButton size="small" onClick={() => setBegin((b) => plusDagen(b, -7))} aria-label="Week terug"><ChevronLeftIcon fontSize="small" /></IconButton>
          <Button size="small" onClick={() => setBegin(new Date())} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, minWidth: 0 }}>Vandaag</Button>
          <IconButton size="small" onClick={() => setBegin((b) => plusDagen(b, 7))} aria-label="Week vooruit"><ChevronRightIcon fontSize="small" /></IconButton>
        </Stack>
        <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{kortDatum(eersteDag)} – {kortDatum(laatsteDag)}</Typography>
        <ToggleButtonGroup size="small" exclusive value={dagenAantal} onChange={(_, v) => v && setDagenAantal(v)} sx={{ bgcolor: '#fff' }}>
          <ToggleButton value={8} sx={{ textTransform: 'none', px: 1.25, fontSize: 12 }}>Week</ToggleButton>
          <ToggleButton value={15} sx={{ textTransform: 'none', px: 1.25, fontSize: 12 }}>2 weken</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek taak, persoon, project…" sx={{ minWidth: 220, bgcolor: '#fff' }} />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <FilterChip aan={verbergWacht} onClick={() => setVerbergWacht((v) => !v)} label="Wacht op feedback / n.v.t. verbergen" />
        <FilterChip aan={verbergAfgerond} onClick={() => setVerbergAfgerond((v) => !v)} label="Afgerond verbergen" />
        <FilterChip aan={verbergVerleden} onClick={() => setVerbergVerleden((v) => !v)} label="Verleden verbergen" />
        <Tooltip title="Gegroepeerd op startdatum en project, zoals de Airtable-view. Balk = start t/m eind, kleurstreep = prio/type (zelfde kleuren als Dagstart en Weekplanning); label = projectnummer-taak · prio · deadline · uren · uitvoerder. Gearchiveerd, opgestuurd en afspraken staan er nooit in. Klik op een taak of project om het project te openen.">
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8', cursor: 'help', textDecoration: 'underline dotted' }}>Hoe lees ik dit?</Typography>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {data && (
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>
            {totaal.projecten} projecten · {totaal.taken} taken · {totaal.uren} u · {totaal.deadlines} deadlines
          </Typography>
        )}
      </Stack>

      {/* ── Tijdlijn ── */}
      <Box ref={kaartRef} sx={{
        border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff', overflow: 'auto', height: `calc(100vh - ${kaartTop + 48}px)`, minHeight: 320,
        // Scrollbar altijd zichtbaar (Joris, 4 sep): onderaan de kaart, ook op macOS/overlay-scrollbars.
        overflowX: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f8fafc',
        '&::-webkit-scrollbar': { height: 12, width: 12 }, '&::-webkit-scrollbar-track': { bgcolor: '#f8fafc' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 6, border: '3px solid #f8fafc' },
        '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#94a3b8' },
      }}>
        <Box sx={{ minWidth: LABEL_BREEDTE + n * dagBreedte, width: LABEL_BREEDTE + n * dagBreedte }}>
          <Rij sx={{ position: 'sticky', top: 0, zIndex: 4, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
            label={<Typography sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Startdatum / project</Typography>}>
            {dagen.map((d, i) => {
              const iso = dagIso[i]; const wk = d.getDay() === 0 || d.getDay() === 6; const isVandaag = iso === vandaag;
              const nieuweMaand = i === 0 || d.getDate() === 1;
              return (
                <Box key={iso} sx={{ flex: 1, textAlign: 'left', pl: 1, py: 0.75, borderLeft: isVandaag ? '2px solid #6366f1' : '1px solid #eef2f6', color: wk ? '#94a3b8' : undefined }}>
                  <Typography sx={{ fontFamily: NAV_FONT, fontSize: 10.5, fontWeight: 800, color: isVandaag ? '#4338ca' : '#64748b', textTransform: 'uppercase' }}>
                    {DAGNAMEN[d.getDay()]}{nieuweMaand ? ` · ${MAANDEN[d.getMonth()]}` : ''}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: isVandaag ? '#4338ca' : '#334155', fontWeight: isVandaag ? 800 : 500 }}>{d.getDate()}</Typography>
                </Box>
              );
            })}
          </Rij>

          {!pl && pls && pls.length === 0 && <Leeg tekst="Geen projectleiders met openstaande projecten gevonden." />}
          {pl && data === null && !fout && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={22} /></Box>}
          {fout && <Leeg tekst={`Laden mislukt: ${fout}`} rood />}
          {data && groepen.length === 0 && !fout && <Leeg tekst={`Niets ingepland voor ${pl} in deze periode (met de huidige filters).`} />}

          {groepen.map((g) => {
            const gSleutel = `d:${g.datum}`; const gDicht = dicht.has(gSleutel);
            const aantal = g.projecten.reduce((s, p) => s + p.taken.length + p.deadlines.length, 0);
            return (
              <Box key={gSleutel}>
                <Rij sx={{ bgcolor: '#eef2f7', borderTop: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => toggleDicht(gSleutel)}
                  label={(
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {gDicht ? <ChevronRightSmallIcon sx={{ fontSize: 16, color: '#64748b' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#64748b' }} />}
                      <Typography sx={{ fontFamily: NAV_FONT, fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>{g.datum ? dmj(g.datum) : 'Zonder startdatum'}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8' }}>{aantal}</Typography>
                    </Stack>
                  )}>
                  <Dagcellen dagIso={dagIso} vandaag={vandaag} hoogte={30} />
                </Rij>
                {!gDicht && g.projecten.map((p) => {
                  const pSleutel = `${gSleutel}|${p.project.id}`; const pDicht = dicht.has(pSleutel);
                  const urenP = Math.round(p.taken.reduce((s, t) => s + (t.uren || 0), 0) * 10) / 10;
                  return (
                    <Box key={pSleutel}>
                      <Rij sx={{ bgcolor: '#fff', borderTop: '1px solid #f1f5f9' }}
                        label={(
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, pl: 1.5 }}>
                            <Box onClick={() => toggleDicht(pSleutel)} sx={{ display: 'flex', cursor: 'pointer' }}>
                              {pDicht ? <ChevronRightSmallIcon sx={{ fontSize: 15, color: '#94a3b8' }} /> : <ExpandMoreIcon sx={{ fontSize: 15, color: '#94a3b8' }} />}
                            </Box>
                            <Typography noWrap onClick={() => navigate(`/projects/${p.project.id}`)} title={`${p.project.nummer}. ${p.project.naam}`}
                              sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#334155', flex: 1, cursor: 'pointer', '&:hover': { color: '#4338ca' } }}>
                              {p.project.nummer}. {p.project.naam}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{p.taken.length}{urenP ? ` · ${urenP} u` : ''}</Typography>
                          </Stack>
                        )}>
                        <Dagcellen dagIso={dagIso} vandaag={vandaag} hoogte={pDicht || p.deadlines.length ? 30 : 8} />
                        {p.deadlines.map((d) => {
                          const i = idxVan(d.deadline); if (i < 0) return null;
                          return (
                            <Tooltip key={d.id} title={`Deadline deliverable (${d.deadlineType || 'deadline'}): ${d.naam} · ${dmj(d.deadline)}`}>
                              <Box sx={{ position: 'absolute', top: 5, left: `calc(${i / n * 100}% + 4px)`, zIndex: 2, display: 'flex', alignItems: 'center', gap: 0.5,
                                bgcolor: '#fef2f2', border: '1px solid #fecaca', borderLeft: '3px solid #dc2626', borderRadius: 1, px: 0.75, height: 20, maxWidth: `calc(${(n - i) / n * 100}% - 8px)` }}>
                                <Typography noWrap sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#991b1b' }}>Deadline · {d.naam}</Typography>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Rij>
                      {!pDicht && p.taken.map((t) => {
                        const pos = balkPositie(t); const kleur = kleurVan(t);
                        const label = [`${p.project.nummer ? `${p.project.nummer}-` : ''}${t.naam}`, t.prio, t.deadline ? dmj(t.deadline) : null, t.uren != null ? uren(t.uren) : null, (t.wie || []).join(', ') || null]
                          .filter(Boolean).join(' · ');
                        return (
                          <Rij key={t.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}
                            label={(
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, pl: 4 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: kleur, flexShrink: 0 }} />
                                <Typography noWrap title={t.naam} sx={{ fontFamily: FONT, fontSize: 12, color: t.afgerond ? '#94a3b8' : '#475569', textDecoration: t.afgerond ? 'line-through' : 'none', flex: 1 }}>{t.naam}</Typography>
                              </Stack>
                            )}>
                            <Dagcellen dagIso={dagIso} vandaag={vandaag} hoogte={RIJ_HOOGTE} />
                            {pos && (
                              <Tooltip title={`${label}${t.start ? ` · ${kortDatum(t.start)} – ${kortDatum(t.eind || t.start)}` : ''}${t.type ? ` · ${t.type}` : ''}`}>
                                <Box onClick={() => navigate(`/projects/${t.projectId}`)} sx={{
                                  position: 'absolute', top: 5, height: 24, left: `calc(${pos.van / n * 100}% + 3px)`, width: `calc(${(pos.tot - pos.van + 1) / n * 100}% - 6px)`,
                                  bgcolor: tint(kleur), border: `1px solid ${rand(kleur)}`, borderLeft: `3px solid ${kleur}`,
                                  borderRadius: 1.5, borderTopLeftRadius: pos.links ? 0 : 6, borderBottomLeftRadius: pos.links ? 0 : 6,
                                  borderTopRightRadius: pos.rechts ? 0 : 6, borderBottomRightRadius: pos.rechts ? 0 : 6,
                                  display: 'flex', alignItems: 'center', pl: 0.75, cursor: 'pointer', overflow: 'visible', zIndex: 1,
                                  opacity: t.afgerond ? 0.7 : 1, '&:hover': { borderColor: kleur, boxShadow: '0 1px 3px rgba(15,23,42,0.12)' },
                                }}>
                                  <Typography sx={{ whiteSpace: 'nowrap', fontFamily: FONT, fontSize: 11.5, fontWeight: 600, color: '#1e293b', lineHeight: '22px', textDecoration: t.afgerond ? 'line-through' : 'none', pr: 1 }}>
                                    {pos.links ? '‹ ' : ''}{label}{pos.rechts ? ' ›' : ''}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Rij>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function FilterChip({ aan, onClick, label }) {
  return (
    <Chip size="small" label={label} onClick={onClick} variant={aan ? 'filled' : 'outlined'}
      sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: aan ? '#eef2ff' : '#fff', color: aan ? '#3730a3' : '#64748b', borderColor: '#e2e8f0' }} />
  );
}

function Leeg({ tekst, rood }) {
  return <Typography sx={{ fontFamily: FONT, fontSize: 13, color: rood ? '#b91c1c' : '#94a3b8', textAlign: 'center', py: 5 }}>{tekst}</Typography>;
}

/** Eén rij: vast label links, tijdlijngebied rechts (relatief, voor balken en ◆). */
function Rij({ label, children, sx, onClick }) {
  return (
    <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'stretch', ...sx }}>
      <Box sx={{ width: LABEL_BREEDTE, flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', borderRight: '1px solid #e2e8f0', position: 'sticky', left: 0, bgcolor: 'inherit', zIndex: 3, minWidth: 0 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>{label}</Box>
      </Box>
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', minWidth: 0, overflow: 'hidden' }}>{children}</Box>
    </Box>
  );
}

function Dagcellen({ dagIso, vandaag, hoogte }) {
  return dagIso.map((iso) => {
    const isVandaag = iso === vandaag;
    return (
      <Box key={iso} sx={{ flex: 1, height: hoogte, borderLeft: isVandaag ? '2px solid #6366f1' : '1px solid #eef2f6' }} />
    );
  });
}
